import { useQuery } from '@tanstack/react-query';
import { teamAPI } from '../api/team.api';
import type { TeamMemberWithPlanning } from '../types/team.types';

interface UseTeamPlanningOptions {
  weekStart: string; // YYYY-MM-DD format (must be a Monday)
}

export const useTeamPlanning = ({ weekStart }: UseTeamPlanningOptions) => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['team-planning', weekStart],
    queryFn: async (): Promise<TeamMemberWithPlanning[]> => {
      return teamAPI.getTeamWeekPlanning(weekStart);
    },
    enabled: !!weekStart,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true
  });

  return {
    teamPlanning: data || [],
    isLoading,
    error,
    refetch
  };
};

/**
 * Helper hook to manage week navigation
 */
export const useWeekNavigation = () => {
  // Get current week's Monday
  const getCurrentMonday = (): Date => {
    const today = new Date();
    const dayOfWeek = today.getDay() || 7; // Sunday = 0 -> 7
    const monday = new Date(today);
    monday.setDate(today.getDate() - dayOfWeek + 1);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const formatDate = (date: Date): string => {
    // Use local date to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getWeekDates = (mondayDate: Date): { date: string; dayOfWeek: string; dayNumber: number }[] => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return days.map((day, index) => {
      const date = new Date(mondayDate);
      date.setDate(mondayDate.getDate() + index);
      return {
        date: formatDate(date),
        dayOfWeek: day,
        dayNumber: date.getDate()
      };
    });
  };

  const getWeekRangeString = (mondayDate: Date): string => {
    const sunday = new Date(mondayDate);
    sunday.setDate(mondayDate.getDate() + 6);

    const monthNames = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];

    const startDay = mondayDate.getDate();
    const endDay = sunday.getDate();
    const startMonth = monthNames[mondayDate.getMonth()];
    const endMonth = monthNames[sunday.getMonth()];
    const year = mondayDate.getFullYear();

    if (mondayDate.getMonth() === sunday.getMonth()) {
      return `${startDay} - ${endDay} ${startMonth} ${year}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  };

  const isCurrentWeek = (mondayDate: Date): boolean => {
    const currentMonday = getCurrentMonday();
    return formatDate(mondayDate) === formatDate(currentMonday);
  };

  return {
    getCurrentMonday,
    formatDate,
    getWeekDates,
    getWeekRangeString,
    isCurrentWeek
  };
};
